<template>
  <div class="flex justify-center">
    <div class="w-full lg:w-8/12">
      <h3 class="mb-0">
        My Profile
      </h3>
      <p class="text-gray-900 text-sm">
        Manage your profile and site settings
      </p>

      <hr class="border-gray-600">

      <section class="mb-8">
        <p class="font-bold mb-4">
          My Information
        </p>
        <d-message v-if="contactInformationError" type="error" class="mb-4">
          {{ contactInformationError }}
        </d-message>
        <d-message v-if="successMessage" type="success" class="mb-4">
          {{ successMessage }}
        </d-message>
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <d-field label="First Name" class="mb-4">
            <d-input 
              v-model="form.firstName" 
              required 
              pattern=".*\S+.*"
              title="First name cannot be only whitespace"
              placeholder="Enter first name"
              class="bg-white"
            />
          </d-field>
          
          <d-field label="Last Name" class="mb-4">
            <d-input 
              v-model="form.lastName" 
              required 
              pattern=".*\S+.*"
              title="Last name cannot be only whitespace"
              placeholder="Enter last name"
              class="bg-white"
            />
          </d-field>
          
          <d-field label="Phone Number" class="mb-4">
            <d-input v-model="form.phoneNumber" placeholder="Enter phone number (optional)" class="bg-white" />
          </d-field>
          
          <d-btn type="submit" :loading="saving" class="mt-4">
            Save Changes
          </d-btn>
        </form>
      </section>
      <hr class="border-gray-600">

      <!-- Email Change Section -->
      <section class="my-10">
        <p class="font-bold mb-4">
          Email Address
        </p>
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p class="text-sm text-gray-900 mb-2">
            <strong>Current Email:</strong> {{ profile.email }}
          </p>
          <p class="text-sm text-gray-900">
            <strong>Important:</strong> Changing your email address may require administrator approval. You will receive confirmation emails at both your current and new email addresses.
          </p>
        </div>

        <d-message v-if="emailChangeError" type="error" class="mb-4">
          {{ emailChangeError }}
        </d-message>
        
        <d-message v-if="emailChangeSuccess" type="success" class="mb-4">
          {{ emailChangeSuccess }}
        </d-message>
        
        <d-message v-if="emailChangePending" type="info" class="mb-4">
          Email change request sent! Please check your new email address ({{ pendingNewEmail }}) for a confirmation link.
        </d-message>

        <div v-if="!emailChangePending">
          <d-field label="New Email Address" class="mb-4">
            <d-input 
              v-model="newEmail" 
              type="email" 
              placeholder="Enter new email address"
              class="bg-white"
            />
          </d-field>
          
          <d-btn :loading="requestingEmailChange" @click="requestEmailChange">
            Request Email Change
          </d-btn>
        </div>
      </section>
      <hr class="border-gray-600">

      <section class="my-10">
        <p class="font-bold mb-5">
          Profile Picture
        </p>
        <div class="flex flex-wrap items-center">
          <div class="w-full md:w-4/12 text-center">
            <div>
              <d-avatar :text="profile.fullName" :img-src="form.profileImageUrl" class="px-4" size="100px" />
            </div>
            <d-btn v-if="form.profileImageUrl" key="removeimage" flat color="error" class="mt-1" @click="showDeleteImagePrompt()">
              Reset Image
            </d-btn>
          </div>
          <div class="w-full md:w-8/12 px-5">
            <input ref="imageUpload" type="file" style="display: none" @change="uploadImage">
            <p class="mb-4">
              Select an image file on your computer (2.5MB max)
            </p>
            <d-btn key="browse" :loading="uploadingFile" @click="$refs.imageUpload.click()">
              Choose Image
            </d-btn>
          </div>
        </div>
      </section>

      <hr class="border-gray-600">

      <section class="my-10">
        <div class="flex items-center">
          <div class="w-7/12">
            <p class="font-bold">
              Password
            </p>
            <p class="text-gray-900 text-sm mb-5">
              Make your password stronger, or change it to continue to keep your account secure.
            </p>
          </div>
          <div class="w-5/12 text-right">
            <d-btn href="/ManageAccount/ChangePassword">
              Change My Password
            </d-btn>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  // Import Pinia auth store
  import { useAuthStore } from '@stores/auth';

  export default {
    parent: true,
    data() {
      return {
        uploadingFile: false,
        saving: false,
        contactInformationError: '',
        successMessage: '',
        newEmail: '',
        requestingEmailChange: false,
        emailChangeError: '',
        emailChangeSuccess: '',
        emailChangePending: false,
        pendingNewEmail: '',
        form: {
          firstName: '',
          lastName: '',
          phoneNumber: '',
          email: '',
          profileImageUrl: '',
          fullName: ''
        },
        auth: null,
      };
    },

    computed: {
      // Use Pinia auth store's profile
      profile() {
        return this.auth ? this.auth.profile || {} : {};
      },
      thumbnailUrl() {
        return this.form.profileImageUrl ? `${this.form.profileImageUrl}?${new Date().getTime()}` : '';
      },
    },

    mounted() {
      this.auth = useAuthStore();
      this.fetchSettings();
    },

    methods: {
      async fetchSettings() {
        const { data } = await axios.get('/api/profile');
        this.form = data;
        if (this.auth && typeof this.auth.setUserProfile === 'function') {
          this.auth.setUserProfile(data);
        }
      },

      async handleSubmit() {
        this.saving = true;
        this.contactInformationError = '';
        this.successMessage = '';
        
        try {
          const updateData = {
            firstName: this.form.firstName?.trim(),
            lastName: this.form.lastName?.trim(),
            phoneNumber: this.form.phoneNumber?.trim()
          };
          
          const { data } = await axios.patch('/api/profile', updateData);
          
          // Update form with returned data
          this.form = {
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            email: data.email,
            profileImageUrl: data.profileImageUrl,
            fullName: data.fullName
          };
          
          // Update auth store
          if (this.auth && typeof this.auth.setUserProfile === 'function') {
            this.auth.setUserProfile(this.form);
          }
          
          this.successMessage = 'Your profile has been updated successfully!';
          this.$toast.success('Your profile has been updated');
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);
        } catch (error) {
          console.error('Save profile error:', error);
          if (error.response && error.response.data) {
            if (Array.isArray(error.response.data)) {
              this.contactInformationError = error.response.data.join(', ');
            } else if (typeof error.response.data === 'string') {
              this.contactInformationError = error.response.data;
            } else {
              this.contactInformationError = 'An error occurred while saving your profile';
            }
          } else {
            this.contactInformationError = 'An error occurred while saving your profile';
          }
          this.$toast.error(this.contactInformationError);
        } finally {
          this.saving = false;
        }
      },

      async uploadImage() {
        this.uploadingFile = true;
        const file = this.$refs.imageUpload.files[0];
        const form = new FormData();
        form.append('image', file);
        try {
          const { data } = await axios.put('/api/profile/image', form, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          this.form.profileImageUrl = data.profileImageUrl;
          if (this.auth && typeof this.auth.setUserProfile === 'function') {
            this.auth.setUserProfile({
              ...this.form,
              fullName: `${this.form.firstName} ${this.form.lastName}`
            });
          }
          this.$toast.success('Image was successfully uploaded');
        } catch (error) {
          this.$toast.error(error.response.data[0]);
        } finally {
          this.resetFileInput();
          this.uploadingFile = false;
        }
      },

      async showDeleteImagePrompt() {
        if (confirm('Are you sure you want to delete your profile picture? This action cannot be undone.')) {
          try {
            await axios.delete('/api/profile/image');
            this.form.profileImageUrl = '';
            if (this.auth && typeof this.auth.setUserProfile === 'function') {
              this.auth.setUserProfile({
                ...this.form,
                fullName: `${this.form.firstName} ${this.form.lastName}`
              });
            }
            this.$toast.success('Profile picture was removed');
          } catch (error) {
            console.error('Delete error:', error);
            this.$toast.error('An error occurred while removing your profile picture');
          }
        }
      },

      async requestEmailChange() {
        if (!this.newEmail) {
          this.emailChangeError = 'Please enter a new email address';
          return;
        }

        this.requestingEmailChange = true;
        this.emailChangeError = '';
        this.emailChangeSuccess = '';
        this.emailChangePending = false;
        
        try {
          await axios.post('/api/profile/email/request', {
            newEmail: this.newEmail
          });
          
          // Show pending state
          this.emailChangePending = true;
          this.pendingNewEmail = this.newEmail;
          this.emailChangeSuccess = 'Email change request sent! Please check your inbox for a confirmation link.';
          this.newEmail = '';
          
          // Clear success message after 10 seconds
          setTimeout(() => {
            this.emailChangeSuccess = '';
          }, 10000);
        } catch (error) {
          console.error('Request email change error:', error);
          if (error.response?.data && Array.isArray(error.response.data)) {
            this.emailChangeError = error.response.data.join(' ');
          } else if (error.response?.data?.message) {
            this.emailChangeError = error.response.data.message;
          } else {
            this.emailChangeError = 'An error occurred while requesting the email change. Please try again.';
          }
        } finally {
          this.requestingEmailChange = false;
        }
      },

      resetFileInput() {
        this.$refs.imageUpload.type = 'text';
        this.$refs.imageUpload.type = 'file';
        this.$refs.imageUpload.value = '';
      },
    },
  };
</script>